import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Search, AlertTriangle, Eye, Ban, CheckCircle, XCircle, Calendar, User, Flag, Shield, Mail, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api-client';

interface Reporter {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface ReportDetails {
  id: string;
  reporter: Reporter;
  reason: string;
  details: string;
  status: string;
  actionTaken: string;
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
}

interface ReportedUser {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    gender: string;
    dateOfBirth: string;
    profilePicture?: string;
    isActive: boolean;
    createdAt: string;
  };
  reports: ReportDetails[];
  totalReports: number;
  pendingReports: number;
  resolvedReports: number;
  lastReportedAt: string;
}

export function AdminReportedUsers() {
  const [reportedUsers, setReportedUsers] = useState<ReportedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGender, setFilterGender] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedUser, setSelectedUser] = useState<ReportedUser | null>(null);
  const [selectedReport, setSelectedReport] = useState<ReportDetails | null>(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [actionTaken, setActionTaken] = useState('none');
  const [newStatus, setNewStatus] = useState('pending');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 1
  });

  useEffect(() => {
    fetchReportedUsers();
  }, [filterGender, filterStatus, pagination.page]);

  const fetchReportedUsers = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (filterGender !== 'all') params.gender = filterGender;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (searchQuery) params.search = searchQuery;

      const data = await apiClient.getReportedUsers(params);

      if (data.success) {
        setReportedUsers(data.data.reportedUsers);
        setPagination(data.data.pagination);
      }
    } catch (error: any) {
      console.error('Error fetching reported users:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to fetch reported users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchReportedUsers();
  };

  const handleUpdateReport = async () => {
    if (!selectedReport) return;

    try {
      const data = await apiClient.updateReportStatus(selectedReport.id, {
        status: newStatus,
        actionTaken,
        reviewNotes
      });

      if (data.success) {
        toast.success('Report updated successfully');
        setReviewDialogOpen(false);
        setReviewNotes('');
        setActionTaken('none');
        setNewStatus('pending');
        fetchReportedUsers();
      }
    } catch (error: any) {
      console.error('Error updating report:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to update report');
    }
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      fake_profile: 'Fake Profile / Misleading Information',
      inappropriate_behavior: 'Inappropriate Behavior',
      harassment: 'Harassment or Abuse',
      spam: 'Spam or Advertising',
      scam: 'Scam or Fraud Attempt',
      underage: 'Underage User',
      offensive_content: 'Offensive or Hateful Content',
      impersonation: 'Impersonation',
      privacy_violation: 'Privacy Violation',
      inappropriate_messages: 'Inappropriate Messages',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      none: 'No Action',
      warning: 'Warning Issued',
      temporary_ban: 'Temporary Ban',
      permanent_ban: 'Permanent Ban',
      profile_deleted: 'Profile Deleted'
    };
    return labels[action] || action;
  };

  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate stats
  const stats = {
    totalReported: reportedUsers.length,
    pendingReports: reportedUsers.reduce((sum, user) => sum + user.pendingReports, 0),
    resolvedReports: reportedUsers.reduce((sum, user) => sum + user.resolvedReports, 0),
    maleReported: reportedUsers.filter(u => u.user.gender === 'male').length,
    femaleReported: reportedUsers.filter(u => u.user.gender === 'female').length
  };

  return (
    <div className="p-6 space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Reported Users', value: stats.totalReported.toString(), color: 'from-red-500 to-rose-500', icon: AlertTriangle },
          { label: 'Pending Reports', value: stats.pendingReports.toString(), color: 'from-orange-500 to-amber-500', icon: Flag },
          { label: 'Resolved Reports', value: stats.resolvedReports.toString(), color: 'from-green-500 to-emerald-500', icon: CheckCircle },
          { label: 'Male Reported', value: stats.maleReported.toString(), color: 'from-blue-500 to-cyan-500', icon: User },
          { label: 'Female Reported', value: stats.femaleReported.toString(), color: 'from-pink-500 to-rose-500', icon: User }
        ].map((stat, index) => (
          <Card key={index} className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                  <p className={`text-3xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                    {stat.value}
                  </p>
                </div>
                <stat.icon className={`size-8 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent opacity-50`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="size-5" />
            Reported Users Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterGender} onValueChange={setFilterGender}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Gender" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genders</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} className="flex items-center gap-2">
              <Search className="size-4" />
              Search
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setFilterGender('all');
                setFilterStatus('all');
                setPagination(prev => ({ ...prev, page: 1 }));
                fetchReportedUsers();
              }}
            >
              Reset Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reported Users Table */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              <p className="mt-2 text-gray-600">Loading reported users...</p>
            </div>
          ) : reportedUsers.length === 0 ? (
            <div className="p-8 text-center">
              <AlertTriangle className="size-12 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">No reported users found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Gender/Age</TableHead>
                  <TableHead>Total Reports</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Reported</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reportedUsers.map((reportedUser) => (
                  <TableRow key={reportedUser.user._id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <ImageWithFallback
                          src={reportedUser.user.profilePicture || ''}
                          alt={`${reportedUser.user.firstName} ${reportedUser.user.lastName}`}
                          className="size-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-medium">
                            {reportedUser.user.firstName} {reportedUser.user.lastName}
                          </p>
                          <Badge variant={reportedUser.user.isActive ? 'default' : 'destructive'} className="text-xs">
                            {reportedUser.user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-1">
                          <Mail className="size-3 text-gray-400" />
                          <span className="text-gray-600">{reportedUser.user.email}</span>
                        </div>
                        {reportedUser.user.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="size-3 text-gray-400" />
                            <span className="text-gray-600">{reportedUser.user.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="capitalize">{reportedUser.user.gender}</p>
                        <p className="text-gray-600">{calculateAge(reportedUser.user.dateOfBirth)} years</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="destructive" className="font-semibold">
                        {reportedUser.totalReports}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={reportedUser.pendingReports > 0 ? 'default' : 'secondary'}>
                        {reportedUser.pendingReports}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={reportedUser.resolvedReports > 0 ? 'secondary' : 'outline'}>
                        {reportedUser.resolvedReports} Resolved
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar className="size-3" />
                        {formatDate(reportedUser.lastReportedAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedUser(reportedUser)}
                        className="flex items-center gap-1"
                      >
                        <Eye className="size-3" />
                        View Reports
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="size-4" />
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.pages, prev.page + 1) }))}
            disabled={pagination.page === pagination.pages}
          >
            Next
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}

      {/* View Reports Dialog */}
      {selectedUser && (
        <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <ImageWithFallback
                  src={selectedUser.user.profilePicture || ''}
                  alt={`${selectedUser.user.firstName} ${selectedUser.user.lastName}`}
                  className="size-12 rounded-full object-cover"
                />
                <div>
                  <p>{selectedUser.user.firstName} {selectedUser.user.lastName}</p>
                  <p className="text-sm font-normal text-gray-600">{selectedUser.totalReports} Total Reports</p>
                </div>
              </DialogTitle>
              <DialogDescription>
                Review and manage reports for this user
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {selectedUser.reports.map((report) => (
                <Card key={report.id} className="border">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-600">Reported By</Label>
                        <p className="font-medium">
                          {report.reporter.firstName} {report.reporter.lastName}
                        </p>
                        <p className="text-sm text-gray-600">{report.reporter.email}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Date</Label>
                        <p className="text-sm">{formatDate(report.createdAt)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Reason</Label>
                        <Badge variant="destructive">{getReasonLabel(report.reason)}</Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">Status</Label>
                        <Badge 
                          variant={
                            report.status === 'resolved' ? 'default' :
                            report.status === 'pending' ? 'secondary' :
                            'outline'
                          }
                        >
                          {report.status}
                        </Badge>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-600">Details</Label>
                        <p className="text-sm">{report.details || 'No additional details provided'}</p>
                      </div>
                      {report.actionTaken !== 'none' && (
                        <div className="col-span-2">
                          <Label className="text-xs text-gray-600">Action Taken</Label>
                          <Badge variant="outline">{getActionLabel(report.actionTaken)}</Badge>
                        </div>
                      )}
                      {report.reviewNotes && (
                        <div className="col-span-2">
                          <Label className="text-xs text-gray-600">Review Notes</Label>
                          <p className="text-sm">{report.reviewNotes}</p>
                        </div>
                      )}
                      <div className="col-span-2 flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedReport(report);
                            setNewStatus(report.status);
                            setActionTaken(report.actionTaken);
                            setReviewNotes(report.reviewNotes || '');
                            setReviewDialogOpen(true);
                          }}
                        >
                          Review Report
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Review Report Dialog */}
      {selectedReport && (
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Review Report</DialogTitle>
              <DialogDescription>
                Update the status and action taken for this report
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label>Report Reason</Label>
                <p className="text-sm font-medium">{getReasonLabel(selectedReport.reason)}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedReport.details}</p>
              </div>

              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="action">Action Taken</Label>
                <Select value={actionTaken} onValueChange={setActionTaken}>
                  <SelectTrigger id="action">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Action</SelectItem>
                    <SelectItem value="warning">Warning Issued</SelectItem>
                    <SelectItem value="temporary_ban">Temporary Ban</SelectItem>
                    <SelectItem value="permanent_ban">Permanent Ban</SelectItem>
                    <SelectItem value="profile_deleted">Profile Deleted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="notes">Review Notes</Label>
                <Textarea
                  id="notes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Add your review notes here..."
                  rows={4}
                />
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateReport}>
                  Update Report
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}